/**
 * Tracking for the onboarding modal (v2) and the Explorer landing.
 *
 * Every modal event goes through trackClick (PostHog `utech_click` + the
 * first-party site_events mirror) with a common set of props:
 *
 *   ob_id          one id per modal mount ("ob_<base36 ms><4 rand>"), so one
 *                  attempt's events join up even when the modal is shown
 *                  again (a reload, ?view=interests)
 *   flow, entry    v1|v2, first_run|resume|deeplink_interests
 *   ms_since_open  client clock since the modal opened
 *
 * Time per step comes from these client ms values and the server's *_at
 * columns, never from site_events.at (that is the beacon's arrival time, up
 * to 30 s late and batched).
 *
 * Props carry ids and choices only — no PII — and are kept under 1 KB: the
 * mirror truncates long props, and a truncated JSON blob used to cost the
 * whole beacon batch.
 */
import { trackClick } from './analytics';
import type { OnbEntry, OnbFlow } from '../types/onboarding';

const MAX_PROPS_BYTES = 1000;

/** A fresh attempt id: "ob_" + base36 milliseconds + 4 random base36 chars. */
export function newObId(): string {
  const rand = Math.random().toString(36).slice(2, 6).padEnd(4, '0');
  return `ob_${Date.now().toString(36)}${rand}`;
}

const size = (v: unknown) => {
  try { return JSON.stringify(v).length; } catch { return Infinity; }
};

/**
 * Keep a props object under ~1 KB: shorten long strings first, then trim
 * arrays, then drop the largest keys. Never throws.
 */
export function capProps(props: Record<string, unknown>, max = MAX_PROPS_BYTES): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.entries(props || {}).forEach(([k, v]) => {
    if (v === undefined) return;
    if (typeof v === 'string') out[k] = v.length > 160 ? v.slice(0, 160) : v;
    else if (Array.isArray(v)) out[k] = v.slice(0, 12).map(x => (typeof x === 'string' && x.length > 80 ? x.slice(0, 80) : x));
    else out[k] = v;
  });
  if (size(out) <= max) return out;
  // Still too big: drop the biggest keys until it fits.
  const keys = Object.keys(out).sort((a, b) => size(out[b]) - size(out[a]));
  for (const k of keys) {
    if (size(out) <= max) break;
    delete out[k];
    out._trimmed = true;
  }
  return out;
}

export interface OnboardingTracker {
  ob_id: string;
  flow: OnbFlow;
  entry: OnbEntry;
  openedAt: number;
  /** Milliseconds since the modal opened. */
  msSinceOpen: () => number;
  /** trackClick(action, {...props, ob_id, flow, entry, ms_since_open}). */
  track: (action: string, props?: Record<string, unknown>) => void;
}

/**
 * One tracker per modal mount. `enabled=false` (the admin preview) returns a
 * tracker whose track() does nothing, so previews never pollute the funnel.
 */
export function createOnboardingTracker(flow: OnbFlow, entry: OnbEntry, enabled = true): OnboardingTracker {
  const ob_id = newObId();
  const openedAt = Date.now();
  const msSinceOpen = () => Math.max(0, Date.now() - openedAt);
  const track = (action: string, props: Record<string, unknown> = {}) => {
    if (!enabled) return;
    try {
      trackClick(action, capProps({ ...props, ob_id, flow, entry, ms_since_open: msSinceOpen() }));
    } catch { /* never let analytics break onboarding */ }
  };
  return { ob_id, flow, entry, openedAt, msSinceOpen, track };
}

/** App-level: the public onboarding config could not be loaded (the modal
 *  then falls back to the legacy flow). Not tied to a modal mount. */
export function trackOnboardingConfigError(status?: number | null, error?: string | null) {
  try {
    trackClick('onboarding_error', capProps({
      stage: 'config', status: status ?? null, error: (error || '').slice(0, 120) || null,
    }));
  } catch { /* never let analytics break onboarding */ }
}

/** Round a client duration into the server's accepted range (0..1 h). */
export function clampMs(ms: number): number {
  if (!isFinite(ms) || ms < 0) return 0;
  return Math.min(3_600_000, Math.round(ms));
}
